/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ColorMap } from '../Attributes';
import type { KaryaButtonTheme } from '../ButtonVariants';

export type ButtonSize = 'ExtraLarge' | 'Large' | 'Medium' | 'Small';
export type BorderRadius = 'Rounded' | 'NonRounded';
export type ButtonVariantType = 'Primary' | 'Secondary' | 'Tertiary' | 'Danger' | 'Success';

export type FullButtonKey = `${ButtonVariantType}/${ButtonSize}/${BorderRadius}`;

export const buttonThemeGenerator = (
  type: ButtonVariantType,
  borderWidth: string,
  borderColor: string,
  backgroundColor: string,
  textColor: string,
  _hover?: object,
  _active?: object,
  _disabled?: object,
): Record<FullButtonKey, object> => {
  const sizePaddingMap: Record<ButtonSize, string> = {
    ExtraLarge: '16px 40px',
    Large: '16px 24px',
    Medium: '12px 20px',
    Small: '8px 16px',
  };

  const roundedOptions: BorderRadius[] = ['Rounded', 'NonRounded'];

  return Object.entries(sizePaddingMap).reduce(
    (acc, [size, padding]) => {
      roundedOptions.forEach((borderRadius) => {
        const key = `${type}/${size}/${borderRadius}` as FullButtonKey;
        acc[key] = {
          borderWidth,
          borderColor,
          backgroundColor,
          textColor,
          borderRadius: borderRadius === 'Rounded' ? '999px' : '12px',
          padding,
          _hover,
          _active,
          _disabled,
        };
      });
      return acc;
    },
    {} as Record<FullButtonKey, object>,
  );
};

export const buttonTheme: KaryaButtonTheme = {
  submit: {
    type: 'submit',
    backgroundColor: ColorMap.primary,
    textColor: ColorMap.white,
    _disabled: {
      backgroundColor: ColorMap.stockGray,
    },
  },
  secondary: {
    backgroundColor: ColorMap.primaryDark,
    textColor: ColorMap.white,
  },
  secondary_outlined: {
    borderColor: ColorMap.primaryDark,
    borderWidth: '1px',
    textColor: ColorMap.primaryDark,
  },
  ...buttonThemeGenerator(
    'Primary',
    '1px',
    ColorMap['Primary/300'],
    ColorMap['Primary/300'],
    ColorMap['Text/50'],
    {
      backgroundColor: ColorMap['Primary/400'],
      borderColor: ColorMap['Primary/400'],
    },
    {
      backgroundColor: ColorMap['Primary/400'],
      borderColor: ColorMap['Primary/400'],
    },
    {
      opacity: 1,
      borderColor: ColorMap['Primary/50'],
      textColor: '#B2B2B2',
      backgroundColor: ColorMap['Background/100'],
    },
  ),
  ...buttonThemeGenerator(
    'Secondary',
    '1px',
    ColorMap['Primary/300'],
    ColorMap.white,
    ColorMap['Primary/300'],
    {
      backgroundColor: ColorMap['Background/50'],
    },
    {
      boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.16) inset',
    },
    {
      opacity: 1,
      borderColor: ColorMap['Dividers/50'],
      textColor: '#B2B2B2',
    },
  ),
  ...buttonThemeGenerator(
    'Tertiary',
    '1px',
    ColorMap.white,
    ColorMap.white,
    ColorMap['Primary/300'],
    {
      backgroundColor: ColorMap['Background/100'],
    },
    {
      boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.16) inset',
      backgroundColor: ColorMap['Background/100'],
    },
    {
      opacity: 1,
      backgroundColor: ColorMap['Background/100'],
      textColor: '#B2B2B2',
    },
  ),
  ...buttonThemeGenerator(
    'Danger',
    '1px',
    ColorMap['Danger/200'],
    ColorMap['Danger/200'],
    ColorMap['Text/50'],
    {
      backgroundColor: ColorMap['Danger/100'],
      borderColor: ColorMap['Danger/100'],
    },
    {
      boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.16) inset',
      backgroundColor: ColorMap['Danger/100'],
      borderColor: ColorMap['Danger/100'],
    },
    {
      opacity: 1,
      backgroundColor: ColorMap['Danger/50'],
      borderColor: ColorMap['Danger/50'],
      textColor: '#B2B2B2',
    },
  ),
  ...buttonThemeGenerator(
    'Success',
    '1px',
    ColorMap['Success/200'],
    ColorMap['Success/200'],
    ColorMap['Text/50'],
    {
      backgroundColor: ColorMap['Success/300'],
      borderColor: ColorMap['Success/300'],
    },
    {
      boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.16) inset',
      backgroundColor: ColorMap['Success/300'],
      borderColor: ColorMap['Success/300'],
    },
    {
      opacity: 1,
      backgroundColor: ColorMap['Success/50'],
      borderColor: ColorMap['Success/50'],
      textColor: '#B2B2B2',
    },
  ),
  'Button/Type1': {
    padding: '6px 12px',
    borderRadius: '999px',
    border: '1px',
    borderColor: ColorMap['Dividers/50'],
    backgroundColor: ColorMap.white,
    textColor: ColorMap['Text/200'],
    _hover: {
      borderColor: ColorMap['Dividers/200'],
    },
    _active: {
      borderColor: ColorMap.TechBlue,
      backgroundColor: ColorMap['TechBlue/50'],
      textColor: ColorMap.TechBlue,
    },
  },
  'Button/Type2': {
    padding: '12px 24px',
    borderRadius: '999px',
    border: '1px',
    borderColor: ColorMap['Dividers/50'],
    backgroundColor: ColorMap.white,
    boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.12)',
    textColor: ColorMap['Text/200'],
    _hover: {
      borderColor: ColorMap['Dividers/200'],
    },
    _active: {
      borderColor: ColorMap['Success/200'],
      backgroundColor: ColorMap['Success/50'],
      textColor: ColorMap['Success/200'],
    },
  },
  'Button/Type3': {
    padding: '16px 40px',
    borderRadius: '999px',
    background: ColorMap['Background/400'],
    textColor: ColorMap.white,
    _hover: {
      _disabled: {
        background: ColorMap['Text/100'],
        textColor: ColorMap['Text/200'],
      },
    },
    _disabled: {
      background: ColorMap['Text/100'],
      textColor: ColorMap['Text/200'],
      opacity: 1,
    },
  },
} as const;
